import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import Typography from "@mui/material/Typography";
import { getErrorMessage, getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import Guid from "_shared/guids/guid";
import LoadingButton from "_shared/_views/loading/loading-button";
import Loader from "_shared/_views/loading/loader";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { type PermissionKind, useGetPermissionKindsQuery } from "permissions/_api/permission-kinds-api-service";
import { type BulkPermissionChangesJSON, useApplyPermissionTemplateMutation } from "permissions/_api/permissions-api-service";
import GrantRow, { GrantRowTable } from "permissions/_views/grant-row/grant-row";
import { type GrantRowModel, buildGrantRow, kindOfResource, resourceLevel } from "permissions/_views/grant-row/grant-row-model";
import useActiveGranteePermissions from "permissions/_views/dialogs/_hooks/use-active-grantee-permissions";
import { sortResourcesForDisplay } from "permissions/_views/_utilities/resource-descriptions";
import { personNameState, usePersonNames } from "permissions/_views/targets/person-name-states";
import { personReference, personStateText } from "permissions/_views/targets/permission-target-state";
import { templateRowModel, withResourceLevel, withResourceSwitched } from "permissions/_views/templates/template-rows-model";
import { REMOVED_WHEN_APPLIED, UNRECOGNIZED_PARENT, kindLabel } from "permissions/_views/wording/permission-wording";
import Permission from "permissions/permission";
import type { Resource } from "permissions/resources/permission-resource";
import { AnyResourceJSON } from "permissions/resources/permission-resource-types";
import { resourceFromJSON } from "permissions/resources/permission-resource-factory";
import PermissionTemplate from "permissions/templates/permission-template";
import {
  buildTemplateApplicationPlan,
  GranteeTemplateChanges,
  TemplateChangeKind,
  TemplateResourceChange,
} from "permissions/templates/template-application-plan";
import {
  type GranteeApplicationSummary,
  type SummaryRow,
  namesSensitiveKind,
  sensitiveRemovals,
  summarizeApplication,
} from "permissions/templates/template-application-summary";
import { useMemo, useState } from "react";
import { useSession } from "users/__session/session-context";

const ButtonContainer = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  justifyContent: "flex-end",
  marginTop: theme.spacing(2),
}));

export type ApplyTemplateDialogProps = {
  template: PermissionTemplate;
  granteeUserIds: Guid[];
  /** Runs once every grantee has been updated, e.g. to clear the table selection behind the dialog. */
  onApplied?: () => void;
};

/**
 * Copies the saved template so edits made here never reach the template itself, in the
 * order the rows are read rather than the order the template happened to store them.
 */
function cloneTemplateResources(template: PermissionTemplate): Resource[] {
  return sortResourcesForDisplay(
    template.resources.flatMap((resource) => {
      const clone = resourceFromJSON(resource.toJSON() as AnyResourceJSON);
      return clone ? [clone] : [];
    }),
  );
}

/**
 * A kind for a resource the kinds endpoint does not describe, so its row is still drawn: the
 * preview and the summary exist to show every grant that changes, and a row dropped silently is the
 * one nobody checks. It is named as an unrecognized parent is, at the level the resource holds.
 */
function unrecognizedKind(resource: Resource): PermissionKind {
  const level = resourceLevel(resource);
  return {
    slug: `unrecognized-${resource.resourceType}`,
    resourceType: resource.resourceType,
    discriminator: null,
    area: "",
    name: UNRECOGNIZED_PARENT,
    allows: "",
    perRecord: false,
    targetNoun: null,
    targetIdProperty: null,
    grantable: false,
    retired: false,
    superUserOnly: false,
    default: null,
    containment: {},
    reach: [],
    leastEffectiveLevel: null,
    controls: [
      ...(level.accessLevel === undefined ? [] : [{ name: "accessLevel" as const, options: [] }]),
      ...(level.scope === undefined ? [] : [{ name: "scope" as const, options: [] }]),
    ],
  };
}

type KnownKind = Readonly<{ kind: PermissionKind; recognized: boolean }>;

function kindOrUnrecognized(kinds: readonly PermissionKind[], resource: Resource): KnownKind {
  const kind = kindOfResource(kinds, resource);
  return kind ? { kind, recognized: true } : { kind: unrecognizedKind(resource), recognized: false };
}

/** The kind's name in the middle of a sentence. */
function kindName(kinds: readonly PermissionKind[], resource: Resource): string {
  return kindOfResource(kinds, resource)?.name ?? UNRECOGNIZED_PARENT;
}

function ChangeSummaryChips(props: Readonly<{ granted: number; removed: number; unchanged: number }>) {
  return (
    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
      <Chip color="success" label={`${props.granted} granted`} size="small" variant="outlined" />
      <Chip color="error" label={`${props.removed} removed`} size="small" variant="outlined" />
      <Chip label={`${props.unchanged} unchanged`} size="small" variant="outlined" />
    </Stack>
  );
}

type SectionRow = Readonly<{
  key: string;
  row: GrantRowModel;
  /** False for a stand-in kind, whose row header is its name alone, with no "may …" sentence. */
  recognized: boolean;
  /** Marks a summary row the preview did not foresee. */
  unexpected?: boolean;
}>;

/** A row header for a kind the kinds endpoint does not describe: the name, and nothing it allows. */
function UnrecognizedHeader(props: Readonly<{ kind: PermissionKind }>) {
  return (
    <Typography className="grant-row-kind-name" variant="body2" sx={{ fontWeight: 500 }}>
      {kindLabel(props.kind)}
    </Typography>
  );
}

/** One titled list of grant rows, in the preview and in the summary alike. */
function GrantRowSection(props: Readonly<{ title: string; rows: readonly SectionRow[]; personName: string }>) {
  const { title, rows, personName } = props;
  if (rows.length === 0) return null;
  return (
    <Box>
      <Typography variant="subtitle2" component="h3" sx={{ mt: 1, px: 2 }}>
        {title} ({rows.length})
      </Typography>
      <GrantRowTable label={`${title}, ${personName}`}>
        {rows.map(({ key, row, recognized, unexpected }) => (
          <GrantRow
            key={key}
            row={row}
            subject="person"
            personName={personName}
            header={recognized ? undefined : <UnrecognizedHeader kind={row.kind} />}
            highlight={unexpected}
            target={unexpected ? <Chip size="small" color="warning" variant="outlined" label="Not in the preview" /> : undefined}
          />
        ))}
      </GrantRowTable>
    </Box>
  );
}

/**
 * A preview row: the grant as it will be once applied, granted at the template's level or removed.
 * Nothing is changed from the preview's rows — the template is adjusted below them — so a row has no
 * switch, no level choices and no lock.
 */
function previewRow(
  change: TemplateResourceChange,
  kinds: readonly PermissionKind[],
  granteeUserId: Guid,
  viewerUserId: Guid,
): SectionRow {
  const { kind, recognized } = kindOrUnrecognized(kinds, change.resource);
  const permission =
    change.existingPermission ??
    new Permission({ resource: change.resource, granteeUserId, grantorUserId: viewerUserId, entityId: Guid.EMPTY, version: 0 });
  const model = buildGrantRow({ kind, kinds, permission, viewerUserId, mayAdministerPlatform: false });
  const removed = change.kind === TemplateChangeKind.Revoked;
  const rowId = `preview-${granteeUserId.value}-${model.rowId}`;
  return {
    key: `${change.kind}-${rowId}-${change.resource.toString()}`,
    recognized,
    row: {
      ...model,
      rowId,
      state: removed ? "removed" : "enabled",
      source: removed ? model.source : undefined,
      removedText: removed ? REMOVED_WHEN_APPLIED : undefined,
      levelOptions: [],
      lock: undefined,
      hasSwitch: false,
    },
  };
}

function GranteeChangeList(
  props: Readonly<{
    grantee: GranteeTemplateChanges;
    kinds: readonly PermissionKind[];
    personName: string;
    viewerUserId: Guid;
  }>,
) {
  const { grantee, kinds, personName, viewerUserId } = props;
  const rows = (kind: TemplateChangeKind) =>
    grantee.changes
      .filter((change) => change.kind === kind)
      .map((change) => previewRow(change, kinds, grantee.granteeUserId, viewerUserId));
  const granted = rows(TemplateChangeKind.Granted);
  const removed = rows(TemplateChangeKind.Revoked);

  if (granted.length + removed.length === 0) {
    return <Typography variant="body2" sx={{ px: 2 }}>Nothing changes for {personName}.</Typography>;
  }

  return (
    <Box>
      <GrantRowSection title="Granted" rows={granted} personName={personName} />
      <GrantRowSection title="Removed" rows={removed} personName={personName} />
    </Box>
  );
}

/**
 * The template's permissions as grant rows, to be adjusted for this one application. A switched-off
 * row removes the kind from whoever holds it; the level chip offers the kind's levels, with what each
 * scope covers beneath it. Every resource gets a row, including one the kinds endpoint does not know.
 */
function AdjustRows(
  props: Readonly<{
    resources: readonly Resource[];
    kinds: readonly PermissionKind[];
    personName: string;
    viewerUserId: Guid;
    mayAdministerPlatform: boolean;
    disabled: boolean;
    onChange: (resources: Resource[]) => void;
  }>,
) {
  const { resources, kinds, personName, viewerUserId, mayAdministerPlatform, disabled, onChange } = props;
  return (
    <GrantRowTable label={`Adjust permissions, ${personName}`}>
      {resources.map((resource, index) => {
        const { kind, recognized } = kindOrUnrecognized(kinds, resource);
        // Keyed by position rather than by the resource, because editing one rewrites it in place
        // and a value-derived key would remount the row mid-edit.
        const key = `${index}-${kind.slug}`;
        const model = templateRowModel({ key, kind, resource, index }, kinds, viewerUserId, mayAdministerPlatform);
        return (
          <GrantRow
            key={key}
            row={{ ...model, removedText: model.removedText && REMOVED_WHEN_APPLIED }}
            subject="person"
            personName={personName}
            header={recognized ? undefined : <UnrecognizedHeader kind={kind} />}
            pending={disabled}
            onSwitch={(on) => onChange(withResourceSwitched(resources, index, on))}
            onChangeLevel={(level) => onChange(withResourceLevel(resources, index, level))}
          />
        );
      })}
    </GrantRowTable>
  );
}

type ApplyPermissionTemplate = ReturnType<typeof useApplyPermissionTemplateMutation>[0];

type ApplyOutcome = {
  savedCount: number;
  failedCount: number;
  message?: string;
  /** The server's answer for each grantee it was given, by user id. */
  answers: Map<string, BulkPermissionChangesJSON>;
};

/**
 * Applies the template to every grantee, one request each so that one grantee's failure does not
 * take the others down with it. The server records each application, and the grants it makes name
 * the template as their source, so no note is stamped on them (#6828).
 */
async function applyTemplate(
  applyPermissionTemplate: ApplyPermissionTemplate,
  template: Readonly<{ id: Guid; version: number }>,
  resources: Resource[],
  granteeUserIds: Guid[],
): Promise<ApplyOutcome> {
  // The version travels with the resources it was read from: a template changed in another tab since
  // is refused, rather than recorded as the version these grants came from.
  const results = await Promise.allSettled(
    granteeUserIds.map((granteeUserId) =>
      applyPermissionTemplate({ templateId: template.id, templateVersion: template.version, granteeUserId, resources }).unwrap(),
    ),
  );

  const failedResults = results.filter((result): result is PromiseRejectedResult => result.status === "rejected");
  const firstFailure = failedResults[0];
  const answers = new Map<string, BulkPermissionChangesJSON>();
  results.forEach((result, index) => {
    if (result.status === "fulfilled") answers.set(granteeUserIds[index].value, result.value);
  });

  return {
    savedCount: results.length - failedResults.length,
    failedCount: failedResults.length,
    message: firstFailure
      ? getErrorMessage(firstFailure.reason) || getErrorMessageFromUnknown(firstFailure.reason)
      : undefined,
    answers,
  };
}

type ApplyStep = "preview" | "confirm" | "summary";

const SENSITIVE_REMOVAL_WARNING =
  "Without signing authority nobody can sign for the organization in their place, and without Member " +
  "Permissions they cannot change anybody's access, their own included.";

const STEPS: Record<ApplyStep, number> = { preview: 0, confirm: 1, summary: 2 };

/** Stands for the kinds while they load or after they failed; one array, so the plan's memo holds. */
const NO_KINDS: readonly PermissionKind[] = [];

/**
 * One list of the summary: every grant the server returned, a kind the kinds endpoint does not know
 * included. Withdrawn grants are never in the preview, so they are not flagged one by one.
 */
function summaryRows(
  rows: readonly SummaryRow[],
  kinds: readonly PermissionKind[],
  viewerUserId: Guid,
  flagUnexpected: boolean,
): SectionRow[] {
  return rows.map(({ permission, expected }, index) => {
    const { kind, recognized } = kindOrUnrecognized(kinds, permission.resource);
    return {
      key: permission.id?.value ?? `${kind.slug}-${index}`,
      row: buildGrantRow({ kind, kinds, permission, viewerUserId, mayAdministerPlatform: false }),
      recognized,
      unexpected: flagUnexpected && !expected,
    };
  });
}

function GranteeSummary(
  props: Readonly<{
    summary: GranteeApplicationSummary;
    kinds: readonly PermissionKind[];
    personName: string;
    viewerUserId: Guid;
  }>,
) {
  const { summary, kinds, personName, viewerUserId } = props;
  const nothing =
    summary.granted.length + summary.raised.length + summary.removed.length + summary.withdrawn.length === 0;
  const section = (title: string, rows: readonly SummaryRow[], flagUnexpected = true) => (
    <GrantRowSection title={title} rows={summaryRows(rows, kinds, viewerUserId, flagUnexpected)} personName={personName} />
  );
  return (
    <Box>
      <Typography variant="subtitle1" component="h2">
        {personName}
      </Typography>
      {nothing && <Typography variant="body2">Nothing changed for {personName}.</Typography>}
      {section("Granted", summary.granted)}
      {section("Raised", summary.raised)}
      {section("Removed", summary.removed)}
      {section("Withdrawn with the removed grants", summary.withdrawn, false)}
      {summary.missing.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          The preview said these would change, and the server left them as they were:{" "}
          {summary.missing.map((change) => kindName(kinds, change.resource)).join(", ")}.
        </Alert>
      )}
    </Box>
  );
}

/**
 * Applies a template in three steps (`docs/design/permissions/PERMISSIONS_UI_DESIGN.md`, "Templates"):
 *
 * 1. **Preview** what it does to each selected user — including the permissions it takes away,
 *    because the server replaces every permission sharing a resource type with the template — read
 *    exactly, through the flat read filtered to the template's resource types. The permissions can
 *    be adjusted first; edits apply to this one application only, and the template is untouched.
 *    Taking away signing authority or Member Permissions asks once more.
 * 2. **Apply**.
 * 3. **A summary the server confirmed**: what was granted, raised, removed and withdrawn behind the
 *    removed grants, drawn with grant rows, with anything the preview did not foresee marked.
 */
export default function ApplyTemplateDialog(props: Readonly<ApplyTemplateDialogProps>) {
  const { template, granteeUserIds, onApplied } = props;

  const session = useSession();
  const { closeDialog } = useDialog();
  const [applyPermissionTemplate] = useApplyPermissionTemplateMutation();

  const [resources, setResources] = useState<Resource[]>(() => cloneTemplateResources(template));
  const [isApplying, setIsApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [step, setStep] = useState<ApplyStep>("preview");
  const [answers, setAnswers] = useState<Map<string, BulkPermissionChangesJSON>>(new Map());
  const kindsQuery = useGetPermissionKindsQuery();

  const isSuperUser = session.user?.isSuperUser ?? false;
  // Edits in the dialog change levels and switches, never which types the template names.
  const templateResourceTypes = useMemo(
    () => template.resources.map((resource) => resource.resourceType as string),
    [template.resources],
  );
  const currentPermissions = useActiveGranteePermissions(granteeUserIds, templateResourceTypes);

  // Memoized on purpose: a fresh set each render would be a fresh lookup argument.
  const granteeKey = granteeUserIds.map((granteeUserId) => granteeUserId.value).join(",");
  const granteeUserIdValues = useMemo(() => new Set(granteeKey ? granteeKey.split(",") : []), [granteeKey]);
  const granteeNames = usePersonNames(granteeUserIdValues);
  // A person the directory did not name: in a sentence, a phrase that reads in the name's place
  // ("a former member"); where the name would be shown, the state that says why (#6429).
  const referTo = (granteeUserId: Guid) => personReference(personNameState(granteeNames, granteeUserId.value));
  const showName = (granteeUserId: Guid) => {
    const state = personNameState(granteeNames, granteeUserId.value);
    return personStateText(state) ?? personReference(state);
  };

  // The kinds say which grants the server replaces as one, so the plan waits for them. Without them
  // every grant of a resource type would count as one kind, and the preview would list removals —
  // and ask about signing authority — for grants the template leaves alone.
  const loadedKinds = kindsQuery.data?.kinds;
  const kinds = loadedKinds ?? NO_KINDS;
  const isKindsLoading = !loadedKinds && !kindsQuery.error;
  const plan = useMemo(
    () =>
      buildTemplateApplicationPlan({
        resources,
        granteeUserIds,
        existingPermissions: currentPermissions.permissions,
        kinds,
      }),
    [resources, granteeUserIds, currentPermissions.permissions, kinds],
  );

  const grantedResourceCount = resources.filter((resource) => !resource.isDisabled).length;
  const isPreviewAvailable = !currentPermissions.isLoading && !currentPermissions.error && loadedKinds !== undefined;
  const viewerUserId = session.user?.id ?? Guid.EMPTY;
  // The edits apply to everybody selected, so a row's controls are named for all of them.
  const adjustingFor =
    granteeUserIds.length === 1
      ? referTo(granteeUserIds[0])
      : `${granteeUserIds.length} selected people`;

  const sensitive = isPreviewAvailable ? sensitiveRemovals(plan.grantees) : [];
  // A preview that failed or was cut short cannot say whether signing authority or Member
  // Permissions go, so a template naming either asks anyway rather than going ahead blind.
  const isPreviewComplete = isPreviewAvailable && !currentPermissions.isTruncated;
  const blindSensitive = !isPreviewComplete && sensitive.length === 0 && namesSensitiveKind(resources);

  function requestApply() {
    if ((sensitive.length > 0 || blindSensitive) && step === "preview") {
      setStep("confirm");
      return;
    }
    void handleApply();
  }

  async function handleApply() {
    if (isApplying) return;

    const currentUser = session.user;
    if (!currentUser?.legalEntityId) {
      setApplyError("Unable to apply the template because the active legal entity is unavailable.");
      return;
    }
    if (!resources.length) {
      setApplyError("This template has no permissions to apply.");
      return;
    }

    setIsApplying(true);
    setApplyError(null);

    const outcome = await applyTemplate(applyPermissionTemplate, template, resources, granteeUserIds);
    setAnswers(outcome.answers);

    if (outcome.failedCount > 0) {
      setIsApplying(false);
      if (outcome.answers.size > 0) setStep("summary");
      const detail = outcome.message ? `: ${outcome.message}` : "";
      const summary =
        outcome.savedCount > 0
          ? `${outcome.savedCount} user(s) updated; ${outcome.failedCount} could not be updated${detail}`
          : `Unable to apply template${detail}`;
      setApplyError(summary);
      enqueueSnackbar(summary, { variant: "warning" });
      return;
    }

    enqueueSnackbar("Permissions updated from template", { variant: "success", role: "status" });
    setIsApplying(false);
    setStep("summary");
    onApplied?.();
  }

  const stepper = (
    <Stepper activeStep={STEPS[step]} sx={{ "& .MuiStepLabel-label": { typography: "body2" } }}>
      <Step completed={step !== "preview"}>
        <StepLabel>Preview</StepLabel>
      </Step>
      <Step completed={step === "summary"}>
        <StepLabel>Apply</StepLabel>
      </Step>
      <Step completed={step === "summary"}>
        <StepLabel>What changed</StepLabel>
      </Step>
    </Stepper>
  );

  if (step === "summary") {
    return (
      <Stack spacing={2}>
        {stepper}
        <Typography variant="body1">
          Applied <strong>{template.name}</strong>. This is what the server changed.
        </Typography>
        {applyError && <Alert severity="error">{applyError}</Alert>}
        {granteeUserIds
          .filter((granteeUserId) => answers.has(granteeUserId.value))
          .map((granteeUserId) => (
            <GranteeSummary
              key={granteeUserId.value}
              summary={summarizeApplication(
                plan.grantees.find((grantee) => grantee.granteeUserId.isEqualTo(granteeUserId)),
                answers.get(granteeUserId.value)!,
              )}
              kinds={kinds}
              personName={showName(granteeUserId)}
              viewerUserId={viewerUserId}
            />
          ))}
        <ButtonContainer>
          <Button variant="contained" onClick={() => closeDialog()}>
            Done
          </Button>
        </ButtonContainer>
      </Stack>
    );
  }

  if (step === "confirm") {
    return (
      <Stack spacing={2}>
        {stepper}
        <Alert severity="warning">
          {blindSensitive &&
            "The preview could not show everything this template takes away, and it names signing authority or Member Permissions, so applying it may take either away."}
          {sensitive
            .map(
              ({ granteeUserId, change }) =>
                `This takes ${kindName(kinds, change.resource)} away from ${referTo(granteeUserId)}.`,
            )
            .join(" ")}{" "}
          {SENSITIVE_REMOVAL_WARNING}
        </Alert>
        {applyError && <Alert severity="error">{applyError}</Alert>}
        <ButtonContainer>
          <Button color="inherit" disabled={isApplying} onClick={() => setStep("preview")}>
            Back
          </Button>
          <LoadingButton
            color="error"
            loading={isApplying}
            variant="contained"
            onClick={() => void handleApply()}
          >
            Apply anyway
          </LoadingButton>
        </ButtonContainer>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      {stepper}
      <Typography variant="body1">
        Applying <strong>{template.name}</strong> to {granteeUserIds.length} user(s). Permissions of the kinds the
        template names, at levels it does not include, will be removed.
      </Typography>

      {(currentPermissions.isLoading || isKindsLoading) && <Loader text="Working out what will change" />}

      {currentPermissions.error && (
        <Alert severity="warning">
          {currentPermissions.error} The template can still be applied, but the change preview is unavailable.
        </Alert>
      )}

      {kindsQuery.error !== undefined && (
        <Alert severity="warning">
          {`Unable to load the kinds of permission. ${getErrorMessageFromUnknown(kindsQuery.error) ?? ""} The template can still be applied, but the change preview is unavailable.`}
        </Alert>
      )}

      {isPreviewAvailable && currentPermissions.isTruncated && (
        <Alert severity="warning">
          These users hold more permissions than the preview could read, so some removals may not be listed.
        </Alert>
      )}

      {isPreviewAvailable && (
        <Box>
          <Stack direction="row" spacing={2} sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 1 }}>
            <Typography variant="subtitle1">Changes</Typography>
            <ChangeSummaryChips
              granted={plan.grantedCount}
              removed={plan.revokedCount}
              unchanged={plan.unchangedCount}
            />
          </Stack>
          {!plan.hasChanges && (
            <Typography color="text.secondary" sx={{ mt: 1 }} variant="body2">
              These users already match this template. Applying it changes nothing.
            </Typography>
          )}
          <Box sx={{ mt: 1 }}>
            {plan.grantees.map((grantee: GranteeTemplateChanges) => {
              const granteeName = referTo(grantee.granteeUserId);
              return (
              <Accordion key={grantee.granteeUserId.value} disableGutters>
                {/* Named explicitly: the chips alone read as a run of bare numbers. */}
                <AccordionSummary aria-label={`Changes for ${granteeName}`} expandIcon={<ExpandMoreIcon />}>
                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 1, width: "100%" }}
                  >
                    <Typography sx={{ flexGrow: 1 }} variant="body2">
                      {showName(grantee.granteeUserId)}
                    </Typography>
                    <ChangeSummaryChips
                      granted={grantee.grantedCount}
                      removed={grantee.revokedCount}
                      unchanged={grantee.unchangedCount}
                    />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0 }}>
                  <GranteeChangeList grantee={grantee} kinds={kinds} personName={granteeName} viewerUserId={viewerUserId} />
                </AccordionDetails>
              </Accordion>
              );
            })}
          </Box>
        </Box>
      )}

      <Divider />

      <Accordion disableGutters>
        <AccordionSummary aria-label="Adjust permissions before applying" expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">
            Adjust permissions before applying ({grantedResourceCount} of {resources.length} granted)
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0 }}>
          <Typography color="text.secondary" sx={{ mb: 1, px: 2 }} variant="body2">
            These changes apply to this one use of the template. The saved template is left as it is.
          </Typography>
          {!resources.length && <Typography variant="body2" sx={{ px: 2 }}>This template has no permissions.</Typography>}
          {resources.length > 0 && (
            <AdjustRows
              resources={resources}
              kinds={kinds}
              personName={adjustingFor}
              viewerUserId={viewerUserId}
              mayAdministerPlatform={isSuperUser}
              disabled={isApplying}
              onChange={setResources}
            />
          )}
        </AccordionDetails>
      </Accordion>

      {applyError && <Alert severity="error">{applyError}</Alert>}

      <ButtonContainer>
        <Button color="inherit" disabled={isApplying} variant="text" onClick={() => closeDialog()}>
          Cancel
        </Button>
        <LoadingButton
          color="primary"
          // Held back while the preview loads: applying before it arrives is exactly the
          // blind change this dialog exists to prevent.
          disabled={isApplying || !resources.length || currentPermissions.isLoading || isKindsLoading}
          loading={isApplying}
          startIcon={<AutoFixHighIcon />}
          variant="contained"
          onClick={requestApply}
        >
          Apply Template
        </LoadingButton>
      </ButtonContainer>
    </Stack>
  );
}
